import { debounce } from "lodash";

function focusPost(posts, index = 0) {
    const post = posts[index];

    if (post) {
        post.classList.add('search-modal__post_focused');
        post.focus();
        post.scrollIntoView();
    }
}

function getElements() {
    const modal = document.querySelector('.search-modal');

    return {
        modal,
        modalContents: modal?.querySelector('.search-modal__content'),
        input: modal?.querySelector('.search-modal__input'),
        results: modal?.querySelector('.search-modal__results'),
        button: modal?.querySelector('.view-all'),
        clearButton: modal?.querySelector('.clear')
    }
}

function platformDependantKeyWrapperInit() {
    const platformDependantKeyWrapper = document.querySelector('.shortcut__button_platform-dependant');

    if (platformDependantKeyWrapper) {
        platformDependantKeyWrapper.textContent = window?.navigator?.userAgentData?.platform === 'macOS'
            ? '\u2318'
            : 'Ctrl';
    }
}

function openModal(modal, input) {
    if (modal) {
        document.body.classList.add('prevent-scroll-search-modal');
        modal.style.display = 'flex';
        input?.focus();
    }
};

function closeModal(modal) {
    if (modal) {
        document.body.classList.remove('prevent-scroll-search-modal');
        modal.style.display = 'none';
    }
};

function clearInput(input) {
    if (input) {
        input.value = '';
    }
}

function handlePostClick(event, section, input, anotherContainer) {
    const container = anotherContainer ?? event.currentTarget;
    const {category, post} = container.dataset;

    clearInput(input);

    window.location = `${window.location.origin}/${section}/${category}/${post}`;
}

function handleShowButton(event, clearButton) {
    if (event.target.value) {
        clearButton.classList.remove('clear_hidden');
    }
}

function curryHandlePostClick(input, section, anotherContainer) {
    return function curryHandlePostClickWithInput(event) {
        return handlePostClick(event, section, input, anotherContainer);
    } 
}

function curryHandleShowButton(clearButton) {
    return function (event) {
        return handleShowButton(event, clearButton)
    }
}

function curryHandleHideButton(search, clearButton) {
    return function handleHideButton(event) {
        search(event.target.value);

        clearButton.classList.toggle('clear_hidden', !event.target.value);
    }
}

function removeElements(elements = [], callback) {
    elements.map(element => {
        callback?.(element);
        element.remove();
    });
}

function createPost(post, input, section) {
    const postContainer = document.createElement('div');
    postContainer.classList = 'search-modal__post'
    postContainer.dataset.category = post.category.code;
    postContainer.dataset.post = post.code;
    
    const handlePostClickWithInput = curryHandlePostClick(input, section);
    postContainer.addEventListener('click', handlePostClickWithInput);
    postContainer.eventHandler = handlePostClickWithInput;

    const postTitle = document.createElement('p');
    postTitle.classList = 'search-modal__post-title';
    postTitle.textContent = post.title;

    const postSubtitle = document.createElement('p');
    postSubtitle.classList = 'search-modal__post-subtitle';
    postSubtitle.textContent = post.subtitle;

    const svgEnterIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svgEnterIcon.classList = 'enter-icon';

    const useEnterIcon = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    useEnterIcon.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '#enter');

    svgEnterIcon.append(useEnterIcon);

    postContainer.append(postTitle);
    postContainer.append(postSubtitle);
    postContainer.append(svgEnterIcon);

    return postContainer;
}

function createCategory(category) {
    const categoryContainer = document.createElement('div');
    categoryContainer.dataset.code = category.code;
    categoryContainer.classList = 'search-modal__category';

    const categoryTitle = document.createElement('h3');
    categoryTitle.classList = 'search-modal__category-title';
    categoryTitle.textContent = category.name;

    categoryContainer.append(categoryTitle);

    return categoryContainer;
}

function renderPartial(posts, modalContents, results, input, section) {
    const postContainers = Array.from(modalContents?.querySelectorAll('.search-modal__post') || []);
    const categoryContainers = Array.from(modalContents?.querySelectorAll('.search-modal__category') || []);

    removeElements(
        postContainers,
        (postContainer) => {
            postContainer.removeEventListener('click', postContainer.eventHandler);
        }
    );
    removeElements(
        categoryContainers
    );

    const containers = posts.map(post => {
        const categoryCode = post.category.code;

        const postContainer = createPost(post, input, section);
        let categoryContainer = results?.querySelector(`[data-code=${categoryCode}]`);

        // create a new category if it doesn't already exist
        if (!categoryContainer) {
            categoryContainer = createCategory(post.category);
            results.append(categoryContainer);
        }

        categoryContainer.append(postContainer);

        return [postContainer, categoryContainer];
    });

    return containers;
}

function renderEmpty(isEmpty) {
    const emptyBlock = document.querySelector('.search-empty__block');
    emptyBlock?.classList.toggle('search-empty__block_visible', isEmpty);
}

async function searchPartial(query, section, modalContents, results, input) {
    try {
        if (query == '') {
            renderEmpty(false);
            return renderPartial([], modalContents, results, input, section);
        }

        const response = await fetch(`${window.location.origin}/${section}/api-search?query=${query}&limit=3`);

        const posts = await response.json();

        renderEmpty(!posts.length);

        renderPartial(posts, modalContents, results, input, section);
    } catch (error) {
        console.log('Search modal error: ', error);
    }
}

function currySearchPartial(section, modalContents, results, input) {
    return function(value) {
        return searchPartial(value, section, modalContents, results, input);
    }
}


function searchAll(query, section, input) {
    clearInput(input);

    window.location = `${window.location.origin}/${section}/search?query=${query}`
}

function curryHandleSearchTabClick(modal) {
    return function curryModalInput(input) {
        return function handleEvent(event) {
            if (event.target.matches('.search-tab')) {
                return openModal(modal, input);
            }
        }
    }
}

export class SearchModal {
    constructor(section) {
        const {
            modal,
            modalContents,
            input,
            results,
            button,
            clearButton
        } = getElements();

        platformDependantKeyWrapperInit();

        input?.addEventListener(
            'input',
            debounce(
                curryHandleHideButton(
                    currySearchPartial(section, modalContents, results, input),
                    clearButton
                ),
                300
            )
        );

        input?.addEventListener('focus', curryHandleShowButton(clearButton));

        input?.addEventListener('focusout', () => {
            clearButton.classList.add('clear_hidden');
        });

        button?.addEventListener('click', () => {
            searchAll(input?.value, section, input);
        });

        const handleSearchWithModal = curryHandleSearchTabClick(modal);
        const handleSearchWithModalInput = handleSearchWithModal(input);        

        // search button is dynamically changed, so event delegation should be used here
        document.addEventListener('click', handleSearchWithModalInput);

        document.addEventListener('keydown', (event) => {
            const metaOrCtrlKey = window?.navigator?.userAgentData?.platform === 'macOS'
                ? event.metaKey
                : event.ctrlKey;

            if (metaOrCtrlKey && event.code == 'KeyK') {
                event.preventDefault();
                return openModal(modal, input);
            }

            if (event.key == 'Escape') {
                return closeModal(modal);
            }

            if (event.key == 'ArrowDown' || event.key == 'ArrowUp') {
                // hide focus icon
                const svgIcon = button?.querySelector('.enter-icon');
                svgIcon.classList.add('enter-icon_hidden');

                const isArrowDownKey = event.key == 'ArrowDown';

                const postContainers = Array.from(modalContents?.querySelectorAll('.search-modal__post') || []);
                const focusedPost = postContainers.find(
                    postContainer => postContainer.classList.contains('search-modal__post_focused')
                );
        
                if (!focusedPost) { 
                    return isArrowDownKey
                        ? focusPost(postContainers)
                        : focusPost(postContainers, postContainers.length - 1);
                }
        
                focusedPost.classList.remove('search-modal__post_focused');
        
                const indexOfFocusedPost = postContainers.indexOf(focusedPost);
                const nextIndex = isArrowDownKey
                    ? indexOfFocusedPost + 1
                    : indexOfFocusedPost - 1;

                if (isArrowDownKey && nextIndex >= postContainers.length) {
                    return focusPost(postContainers);
                }

                if (!isArrowDownKey && nextIndex < 0) {
                    return focusPost(postContainers, postContainers.length - 1);
                }
    
                return focusPost(postContainers, nextIndex);
            }

            if (event.key == 'Enter') {
                const focusedPost = modalContents.querySelector('.search-modal__post_focused');

                if (focusedPost) {
                    handlePostClick(event, section, input, focusedPost);
                } else {
                    searchAll(input?.value, section, input)
                }
            }
        })

        modalContents?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
        });

        modal?.addEventListener('click', () => closeModal(modal));

        clearButton?.addEventListener('mousedown', () => {
            clearInput(input);
            searchPartial(input?.value, section, modalContents, results, input);
        });
    }
}
